import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Flag, Phone, LifeBuoy, ShieldCheck, RotateCcw } from "lucide-react";
import { api, errMsg, formatIDR, formatDuration, formatTime, STATUS_LABEL } from "@/lib/api";
import { Avatar, StatusBadge, Countdown } from "@/components/shared";

const STEPS = ["awaiting_payment", "paid", "accepted", "in_session", "completed"];

function RatingForm({ code, onDone }) {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const submit = async () => {
    if (!score) return toast.error("Pilih bintang dulu");
    try { await api.post(`/orders/${code}/rating`, { score, comment }); toast.success("Terima kasih atas rating kamu"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-soft p-6" data-testid="rating-form">
      <h3 className="font-heading text-lg font-medium text-slate-800">Bagaimana sesinya?</h3>
      <div className="mt-3 flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setScore(n)} className="p-1 transition-transform active:scale-90" data-testid={`rating-star-${n}`} aria-label={`${n} bintang`}>
            <Star size={28} strokeWidth={1.5} className={n <= score ? "text-amber-500 fill-amber-400" : "text-slate-300"} />
          </button>
        ))}
      </div>
      <textarea className="input-soft mt-4 h-24 py-3" placeholder="Ceritakan pengalamanmu (opsional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="rating-comment-input" />
      <button onClick={submit} className="btn-primary mt-4" data-testid="rating-submit-button">Kirim rating</button>
    </div>
  );
}

function ReportDialog({ code, onClose }) {
  const [reason, setReason] = useState("Pelecehan / konten seksual");
  const [details, setDetails] = useState("");
  const submit = async () => {
    try { await api.post(`/orders/${code}/report`, { reason, details }); toast.success("Laporan terkirim. Admin akan menindaklanjuti."); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="report-dialog">
        <h3 className="font-heading text-lg font-medium text-slate-800">Laporkan talent</h3>
        <select className="input-soft mt-4" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="report-reason-select">
          {["Pelecehan / konten seksual", "Talent tidak muncul", "Transaksi di luar platform", "Tidak profesional", "Lainnya"].map((r) => <option key={r}>{r}</option>)}
        </select>
        <textarea className="input-soft mt-3 h-24 py-3" placeholder="Ceritakan kejadiannya" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details-input" />
        <div className="mt-4 flex gap-2 justify-end"><button className="btn-ghost" onClick={onClose} data-testid="report-cancel-button">Batal</button><button className="btn-primary h-10" onClick={submit} data-testid="report-submit-button">Kirim laporan</button></div>
      </div>
    </div>
  );
}

export default function OrderStatus() {
  const { code } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [report, setReport] = useState(false);

  const load = useCallback(() => api.get(`/orders/${code}`).then((r) => { setOrder(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), [code]);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);

  if (err) return <div className="max-w-xl mx-auto px-4 py-24 text-center"><p className="text-slate-600" data-testid="order-error">{err}</p><Link to="/cek-order" className="btn-outline mt-6">Coba kode lain</Link></div>;
  if (!order) return <div className="py-24 text-center text-slate-500">Memuat...</div>;

  const stepIdx = STEPS.indexOf(order.status);
  const terminalBad = ["expired", "cancelled", "refunded"].includes(order.status);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <p className="eyebrow mb-3">Status order</p>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-800 tracking-wide" data-testid="order-code">{order.code}</h1>
        <StatusBadge status={order.status} />
      </div>

      {!terminalBad && (
        <ol className="mt-8 grid grid-cols-5 gap-1" data-testid="order-stepper">
          {STEPS.map((s, i) => (
            <li key={s} className="flex flex-col gap-2">
              <span className={`h-1.5 rounded-full ${i <= stepIdx ? "bg-primary" : "bg-slate-200"}`} />
              <span className={`text-[11px] leading-tight ${i <= stepIdx ? "text-slate-700" : "text-slate-400"}`}>{STATUS_LABEL[s]}</span>
            </li>
          ))}
        </ol>
      )}

      <div className="mt-8 card-soft p-6" data-testid="order-summary">
        <div className="flex items-center gap-4">
          <Avatar src={order.talent?.avatar_url} name={order.talent_name} size={56} online={order.talent?.is_online} />
          <div><p className="font-heading font-medium text-slate-800">{order.talent_name}</p><p className="text-sm text-slate-500">{order.service_name} · {formatDuration(order.duration_minutes)} · {formatIDR(order.price)}</p></div>
        </div>
        <dl className="mt-6 grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-slate-500">Nama panggilan</dt><dd className="text-slate-800">{order.nickname}</dd>
          <dt className="text-slate-500">WhatsApp kamu</dt><dd className="text-slate-800" data-testid="order-whatsapp-masked">{order.whatsapp}</dd>
          <dt className="text-slate-500">Jadwal sesi</dt><dd className="text-slate-800" data-testid="order-scheduled-at">{order.is_immediate ? "Sesegera mungkin" : `${formatTime(order.scheduled_at)} WIB`}</dd>
          <dt className="text-slate-500">Dibuat</dt><dd className="text-slate-800">{formatTime(order.created_at)}</dd>
        </dl>

        {order.status === "awaiting_payment" && (
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
            <span className="text-sm text-amber-900">Bayar sebelum <Countdown target={order.expires_at} onDone={load} /></span>
            <div className="flex gap-2">
              <button onClick={async () => { if (!window.confirm("Batalkan order ini?")) return; try { await api.post(`/orders/${order.code}/cancel`); toast.success("Order dibatalkan"); load(); } catch (e) { toast.error(errMsg(e)); } }} className="btn-ghost h-10 text-sm" data-testid="order-cancel-button">Batalkan</button>
              <Link to={`/pay/${order.code}`} className="btn-primary h-10 px-5" data-testid="order-pay-link">Bayar sekarang</Link>
            </div>
          </div>
        )}
        {["paid", "accepted", "in_session"].includes(order.status) && order.talent?.whatsapp_business && (
          <div className="mt-6 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="order-official-number">
            <p className="font-medium inline-flex items-center gap-1.5"><ShieldCheck size={15} strokeWidth={1.5} />Nomor resmi yang akan menghubungi kamu</p>
            <p className="mt-1 inline-flex items-center gap-2"><Phone size={14} strokeWidth={1.5} /><span className="font-heading font-semibold tracking-wide" data-testid="order-official-number-value">+{order.talent.whatsapp_business}</span><span className="text-sky-700">· milik Sudut Nadi, dipakai {order.talent_name}</span></p>
            <p className="mt-1 text-xs text-sky-800">Abaikan nomor lain yang mengaku dari Sudut Nadi atau mengajak transaksi di luar platform.</p>
          </div>
        )}
        {order.status === "paid" && (
          <p className="mt-6 rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-900" data-testid="order-waiting-talent">
            Pembayaran berhasil. Menunggu {order.talent_name} menerima order (maks. <Countdown target={order.accept_deadline} onDone={load} />). Jika tidak direspons, order otomatis dialihkan ke talent lain atau dana dikembalikan penuh.
          </p>
        )}
        {order.status === "accepted" && (
          <div className="mt-6 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900" data-testid="order-accepted-info">
            <p>{order.talent_name} sudah menerima dan akan menghubungi kamu dari nomor resmi di atas. Sebutkan kode <strong>{order.code}</strong> saat dihubungi.</p>
          </div>
        )}
        {order.status === "in_session" && (
          <div className="mt-6 rounded-lg bg-secondary px-4 py-4 text-center" data-testid="order-session-timer">
            <p className="text-sm text-slate-600">Sesi berjalan, sisa waktu</p>
            <Countdown target={order.session_ends_at} className="text-3xl text-slate-800" onDone={load} />
          </div>
        )}
        {order.status === "refunded" && <p className="mt-6 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" data-testid="order-refund-info">Dana dikembalikan penuh. Alasan: {order.refund_reason}</p>}
        {order.status === "expired" && <p className="mt-6 text-sm text-slate-600" data-testid="order-expired-info">Order kedaluwarsa karena pembayaran tidak diselesaikan dalam 15 menit. <Link to="/talent" className="underline text-primary">Buat order baru</Link></p>}
        {order.status === "cancelled" && <p className="mt-6 text-sm text-slate-600" data-testid="order-cancelled-info">Order ini dibatalkan. Slot talent sudah dilepas. <Link to="/talent" className="underline text-primary">Buat order baru</Link></p>}
        {order.rating && <p className="mt-6 text-sm text-slate-600 inline-flex items-center gap-2" data-testid="order-rating-done"><Star size={14} className="text-amber-500 fill-amber-400" strokeWidth={1.5} />Kamu memberi {order.rating.score} bintang. Terima kasih.</p>}
        {order.status === "completed" && order.talent && (
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap rounded-lg bg-secondary px-4 py-3" data-testid="order-rebook">
            <p className="text-sm text-slate-700">Cocok dengan {order.talent_name}? Pesan lagi lewat Sudut Nadi supaya tetap aman dan terjamin.</p>
            <Link to={`/booking/${order.talent.id}?service=${order.service_id}&duration=${order.duration_minutes}`} className="btn-primary h-10 px-4" data-testid="order-rebook-button"><RotateCcw size={15} strokeWidth={1.5} />Pesan lagi talent ini</Link>
          </div>
        )}
      </div>

      {order.can_rate && <div className="mt-6"><RatingForm code={order.code} onDone={load} /></div>}

      <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
        <button onClick={() => setReport(true)} className="btn-ghost text-rose-700 hover:bg-rose-50" data-testid="order-report-button"><Flag size={16} strokeWidth={1.5} />Laporkan</button>
        <p className="text-xs text-slate-500 inline-flex items-center gap-1.5"><LifeBuoy size={14} strokeWidth={1.5} />Darurat: 112 · Kemenkes 119 ext. 8</p>
      </div>

      <div className="mt-10">
        <p className="eyebrow mb-3">Riwayat</p>
        <ul className="space-y-2 text-sm" data-testid="order-timeline">
          {order.timeline?.slice().reverse().map((t, i) => <li key={i} className="flex gap-3"><span className="text-slate-400 shrink-0 w-32">{formatTime(t.at)}</span><span className="text-slate-700">{STATUS_LABEL[t.status] || t.status}{t.note ? ` — ${t.note}` : ""}</span></li>)}
        </ul>
      </div>
      {report && <ReportDialog code={order.code} onClose={() => setReport(false)} />}
    </div>
  );
}
